import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { landingCopy } from '../i18n/landingCopy';

const STORAGE_KEY = 'simopay-landing-locale';

function getByPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

const LandingLanguageContext = createContext(null);

export function LandingLanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === 'fr' || saved === 'en' || saved === 'ar') return saved;
    } catch {
      /* ignore */
    }
    return 'en';
  });

  useEffect(() => {
    const langMap = { fr: 'fr', ar: 'ar', en: 'en' };
    document.documentElement.lang = langMap[locale] || 'en';
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (next !== 'en' && next !== 'fr' && next !== 'ar') return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (path) => {
      const bundle = landingCopy[locale] || landingCopy.en;
      const value = getByPath(bundle, path);
      if (value !== undefined) return value;
      const fallback = getByPath(landingCopy.en, path);
      return fallback !== undefined ? fallback : path;
    },
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return (
    <LandingLanguageContext.Provider value={value}>
      {children}
    </LandingLanguageContext.Provider>
  );
}

export function useLandingLanguage() {
  const ctx = useContext(LandingLanguageContext);
  if (!ctx) {
    throw new Error('useLandingLanguage must be used within LandingLanguageProvider');
  }
  return ctx;
}
